/**
 * 富山県の背景ツアー「富山めぐり」コンテンツ。
 *
 * 軸は一日の時間。冬の朝の雨晴海岸から、春の深夜のほたるいか漁へ。
 * 最後だけ時間が一日を越えて深夜になる。ここは富山でしか撮れない絵なので例外にした。
 *
 * 五箇山の合掌造りは扱わない。
 *  世界遺産「白川郷・五箇山の合掌造り集落」の絵は岐阜の白川郷に割り当てた。
 *  同じ形の集落を二つ出すと絵がぶつかる。
 *
 * 「日本一」は書かない。
 *  雪の大谷について、立山黒部アルペンルート公式と県の観光公式にあるのは
 *  「世界有数の豪雪地帯」まで。「日本一の雪の壁」とは書かない。
 *  雪壁の高さ「15〜20メートル」「最大20メートル」は県公式観光サイトの表現。
 *
 * 企業の施設と車両は出さない。
 *  黒部ダムは関西電力の施設なので扱わない。
 *  黒部峡谷は鉄道会社の車両を描かず、峡谷と鉄橋だけにする。
 *  雪の大谷も除雪車とバスを描かない。
 *  海王丸は1930年進水の船なので問題ないが、パークの現代の設置物は入れない。
 *
 * おわら風の盆は扱わない。
 *  踊り手が多数写る行事で、公式ページを確認できていない。
 *  八尾は「人のいない夕暮れの諏訪町本通り」として、坂と石垣だけを描く。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 黒部峡谷の国の特別名勝・特別天然記念物指定は公式で確認できなかった。
 *  - ホタルイカ群遊海面が特別天然記念物に指定された年は確認できなかった。
 *  - 帆船海王丸の重要文化財指定の有無は公式に記載がない。
 *  - 山町筋・岩瀬の重伝建選定年も確認できなかった。
 *  - チューリップタワーの高さ、フェアの日程も公式に記載がない。
 */

import type { StageTour } from '../types';

export const toyamaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'amaharashi',
      name: '雨晴海岸',
      area: '高岡市・冬の朝',
      image: '/assets/scenery/toyama/scenes/01_amaharashi.webp',
      hotspots: [
        {
          id: 'amaharashi-meisho',
          x: 0.5,
          y: 0.6,
          title: '国指定名勝「有磯海」',
          body: '雨晴海岸は国指定名勝「おくのほそ道の風景地ー有磯海ー」で、県指定ふるさと眺望点にもなっています。日本の渚百選、白砂青松百選、日本の海水浴場88選にも選ばれています。',
          source: 'https://www.takaoka.or.jp/viewpoint/detail_3129.html',
          mapQuery: '雨晴海岸',
        },
        {
          id: 'amaharashi-yoshitsune',
          x: 0.24,
          y: 0.44,
          title: '地名の由来',
          body: '源義経が奥州へ落ちのびる途中、にわか雨の晴れるのを待ったという義経岩が、地名「雨晴」の由来ともなっています。',
          source: 'https://www.takaoka.or.jp/viewpoint/detail_3129.html',
          mapQuery: '雨晴 義経岩',
        },
        {
          id: 'amaharashi-11gatsu',
          x: 0.76,
          y: 0.3,
          title: '11月から3月がいちばん美しい',
          body: '立山連峰がもっとも美しく見える時期は11月から3月頃の冬の晴れた日です。冬には気嵐という霧のような現象も見られます。',
          source: 'https://www.takaoka.or.jp/viewpoint/detail_3129.html',
          mapQuery: '雨晴海岸 立山連峰',
        },
      ],
    },
    {
      id: 'yuki-no-otani',
      name: '雪の大谷',
      area: '立山町・春の午前',
      image: '/assets/scenery/toyama/scenes/02_yuki_no_otani.webp',
      hotspots: [
        {
          id: 'otani-kikan',
          x: 0.5,
          y: 0.5,
          title: 'アルペンルートの春の風物詩',
          body: '雪の大谷ウォークは、アルペンルートの春の風物詩です。大迫力の雪壁を間近で感じることができます。',
          source: 'https://www.alpen-route.com/enjoy_navi/snow_otani/',
          mapQuery: '雪の大谷',
        },
        {
          id: 'otani-20m',
          x: 0.24,
          y: 0.36,
          title: '高さ15から20メートルの雪壁',
          body: '高さ最大20メートルにもなる雪の壁です。歩行区間は約500メートル、往復約35分かかります。',
          source: 'https://www.info-toyama.com/stories/yuki_otani',
          mapQuery: '立山 室堂',
        },
        {
          id: 'otani-gousetsu',
          x: 0.76,
          y: 0.62,
          title: '世界有数の豪雪地帯',
          body: '標高2,450メートルの室堂付近は世界有数の豪雪地帯で、積雪が20メートルを超えることもあります。',
          source: 'https://www.info-toyama.com/stories/yuki_otani',
          mapQuery: '立山黒部アルペンルート',
        },
      ],
    },
    {
      id: 'shomyodaki',
      name: '称名滝',
      area: '立山町・昼前',
      image: '/assets/scenery/toyama/scenes/03_shomyodaki.webp',
      hotspots: [
        {
          id: 'shomyo-350m',
          x: 0.5,
          y: 0.44,
          title: '落差350メートル',
          body: '称名滝は日本一の落差350メートル。環境省は「東京タワー333メートルがすっぽり入ってしまう高さです」と紹介しています。',
          source: 'https://chubu.env.go.jp/blog/2020/06/post-967.html',
          mapQuery: '称名滝',
        },
        {
          id: 'shomyo-4dan',
          x: 0.24,
          y: 0.58,
          title: '4段になって落ちる',
          body: '滝は4段になっており、上から70メートル、58メートル、96メートル、126メートルと順に落ちています。国の名勝および天然記念物で、日本の滝百選にも選ばれています。',
          source: 'https://chubu.env.go.jp/blog/2020/06/post-967.html',
          mapQuery: '称名滝 落差',
        },
        {
          id: 'shomyo-hannoki',
          x: 0.76,
          y: 0.3,
          title: '融雪期だけ現れるハンノキ滝',
          body: '融雪期や降雨時には、称名滝の右手にハンノキ滝が現れます。約7万年前、滝は現在の立山駅付近にあり、水の流れの浸食で徐々に後退して今の位置に移りました。',
          source: 'https://chubu.env.go.jp/blog/2020/06/post-967.html',
          mapQuery: 'ハンノキ滝',
        },
      ],
    },
    {
      id: 'tonami-tulip',
      name: '砺波チューリップ公園',
      area: '砺波市・春の昼',
      image: '/assets/scenery/toyama/scenes/04_tonami_tulip.webp',
      hotspots: [
        {
          id: 'tulip-300man',
          x: 0.5,
          y: 0.56,
          title: '300品種300万本',
          body: 'フェア期間中は300品種、300万本のチューリップが咲き誇ります。',
          source: 'https://www.city.tonami.lg.jp/section/1607p/',
          mapQuery: '砺波チューリップ公園',
        },
        {
          id: 'tulip-gorensuisha',
          x: 0.24,
          y: 0.4,
          title: '日本最大級の五連水車',
          body: '園内には日本最大級を誇る五連水車があります。',
          source: 'https://www.city.tonami.lg.jp/section/1607p/',
          mapQuery: '砺波市 五連水車',
        },
        {
          id: 'tulip-tsunen',
          x: 0.76,
          y: 0.32,
          title: 'フェア以外は入園無料',
          body: '春の「となみチューリップフェア」には毎年約30万人が訪れます。フェア期間以外は入園無料ですが、フェアの開催前10日間と終了後7日間は準備と片付けのため入園できません。',
          source: 'https://www.city.tonami.lg.jp/section/1607p/',
          mapQuery: '砺波市',
        },
      ],
    },
    {
      id: 'yatsuo-suwamachi',
      name: '八尾 諏訪町本通り',
      area: '富山市・午後',
      image: '/assets/scenery/toyama/scenes/05_yatsuo_suwamachi.webp',
      hotspots: [
        {
          id: 'yatsuo-saka',
          x: 0.5,
          y: 0.5,
          title: '石を積み上げた坂の町',
          body: '八尾は山の傾斜に石を積み上げた上に細長くできた坂の町で、石畳の道と格子戸や白壁の町並みが続きます。',
          source:
            'https://www.pref.toyama.jp/1507/kendodukuri/toshikeikaku/keikaku-tochi/kj00022941/kj00022941-016-33.html',
          mapQuery: '八尾 諏訪町本通り',
        },
        {
          id: 'yatsuo-ishigaki',
          x: 0.24,
          y: 0.64,
          title: '高さ約30メートルの石垣',
          body: '石垣の高さは約30メートル。石垣の起源は江戸時代に川の氾濫から逃れようと住民が高台に町を開いたことにあります。',
          source: 'https://www.info-toyama.com/attractions/90211',
          mapQuery: '越中八尾',
        },
        {
          id: 'yatsuo-koeki',
          x: 0.76,
          y: 0.34,
          title: '街道の拠点として栄えた町',
          body: '八尾町はかつて街道の拠点として、飛騨との交易や売薬、売薬用紙の販売、養蚕による収益などで繁栄した経済力豊かな町でした。名は、この地が飛騨の山々から富山へのびる八つの山にひらかれたことに由来するといわれています。',
          source:
            'https://www.pref.toyama.jp/1507/kendodukuri/toshikeikaku/keikaku-tochi/kj00022941/kj00022941-016-33.html',
          mapQuery: '富山市 八尾町',
        },
      ],
    },
    {
      id: 'inami-yokamachi',
      name: '井波 八日町通り',
      area: '南砺市・午後',
      image: '/assets/scenery/toyama/scenes/06_inami_yokamachi.webp',
      hotspots: [
        {
          id: 'inami-600nen',
          x: 0.5,
          y: 0.5,
          title: '600年の門前町',
          body: '600年の歴史を誇る井波別院瑞泉寺から伸びる石畳の通りです。',
          source: 'https://www.info-toyama.com/attractions/80089',
          mapQuery: '井波 八日町通り',
        },
        {
          id: 'inami-chokoku',
          x: 0.24,
          y: 0.38,
          title: '井波彫刻の工房',
          body: '数多くの井波彫刻の工房が軒を連ね、木彫りのバス標識や表札など、随所に伝統技術を用いた井波彫刻を見ることができます。',
          source: 'https://www.info-toyama.com/attractions/80089',
          mapQuery: '井波彫刻',
        },
        {
          id: 'inami-oto',
          x: 0.76,
          y: 0.64,
          title: '残したい日本の音風景100選',
          body: '職人の打つ木槌とノミの音が聞こえ、その音は環境省が認定する「残したい日本の音風景100選」に選ばれています。',
          source: 'https://www.info-toyama.com/attractions/80089',
          mapQuery: '南砺市 井波',
        },
      ],
    },
    {
      id: 'kurobe-atobiki',
      name: '黒部峡谷 後曳橋',
      area: '黒部市・秋の午後',
      image: '/assets/scenery/toyama/scenes/07_kurobe_atobiki.webp',
      hotspots: [
        {
          id: 'kurobe-vjikoku',
          x: 0.5,
          y: 0.46,
          title: '日本一深いといわれるV字峡谷',
          body: '黒部峡谷は日本一深いといわれるV字峡谷で、中部山岳国立公園に含まれます。黒部川は約86キロ、トロッコ電車は約20キロを片道約80分で運行します。',
          source: 'https://www.info-toyama.com/attractions/51007',
          mapQuery: '黒部峡谷',
        },
        {
          id: 'kurobe-atobiki',
          x: 0.24,
          y: 0.62,
          title: '川底まで約60メートル',
          body: '後曳橋は黒薙川の川底まで約60メートル、沿線で最も険しい谷に架かる橋です。',
          source: 'https://www.kurotetu.co.jp/spot_all/',
          mapQuery: '後曳橋',
        },
        {
          id: 'kurobe-senro',
          x: 0.76,
          y: 0.32,
          title: '最初に渡る真紅の鉄橋',
          body: '宇奈月駅を出発して最初に渡るのが、真紅の鉄橋・新山彦橋です。渓流からの高さは約40メートルあります。',
          source: 'https://www.kurotetu.co.jp/spot_all/',
          mapQuery: '黒部峡谷鉄道',
        },
      ],
    },
    {
      id: 'sankyoson',
      name: '散居村 砺波平野',
      area: '砺波市・夕',
      image: '/assets/scenery/toyama/scenes/08_sankyoson.webp',
      hotspots: [
        {
          id: 'sankyo-kainyo',
          x: 0.5,
          y: 0.54,
          title: 'カイニョと呼ばれる屋敷林',
          body: '家はカイニョと呼ばれる屋敷林に囲まれています。木の種類は杉を中心に、ケヤキや竹、カキの木などが植えられています。南西の強風から住まいを守り、冬は積雪を防ぎ、夏は日差しを遮ります。',
          source:
            'https://www.pref.toyama.jp/1021/kensei/kenseiunei/kensei/naruhodo/treasure/takara4.html',
          mapQuery: '砺波平野 散居村',
        },
        {
          id: 'sankyo-yurai',
          x: 0.24,
          y: 0.4,
          title: 'なぜ離れて建つのか',
          body: '成り立ちには諸説あり、風が強く火事になったときに隣の家に燃え広がらないようにするため、農作業をしやすいように自分の家の周りを田んぼにしていったため、などと説明されています。',
          source:
            'https://www.pref.toyama.jp/1021/kensei/kenseiunei/kensei/naruhodo/treasure/takara4.html',
          mapQuery: '砺波 散居村',
        },
        {
          id: 'sankyo-tenbodai',
          x: 0.76,
          y: 0.3,
          title: '展望台からの眺め',
          body: '散居村展望台からは砺波平野の散居村を一望でき、立山連峰や、はるか日本海まで見渡せることもあります。富山県の「ふるさと眺望点」に指定されています。',
          source: 'https://www.city.tonami.lg.jp/kanko/midokoro/672p/',
          mapQuery: '砺波 散居村展望台',
        },
      ],
    },
    {
      id: 'kaiwomaru',
      name: '海王丸パーク',
      area: '射水市・日没',
      image: '/assets/scenery/toyama/scenes/09_kaiwomaru.webp',
      hotspots: [
        {
          id: 'kaiwomaru-1930',
          x: 0.5,
          y: 0.48,
          title: '昭和5年進水',
          body: '海王丸は1930年（昭和5年）2月14日進水の4本マストバーク型帆船です。全長97.05メートル、型幅12.95メートル、メインマストは海面から46メートル、総トン数2,238.40トン。',
          source: 'https://www.kaiwomaru.jp/kaiwomaru/',
          mapQuery: '海王丸パーク',
        },
        {
          id: 'kaiwomaru-sails',
          x: 0.24,
          y: 0.36,
          title: '総帆数29枚',
          body: '総帆数は29枚、帆の面積は2,050平方メートルで、約1,245畳分になります。',
          source: 'https://www.kaiwomaru.jp/kaiwomaru/',
          mapQuery: '帆船海王丸',
        },
        {
          id: 'kaiwomaru-park',
          x: 0.76,
          y: 0.64,
          title: '展望広場からの眺め',
          body: '海王丸パークの展望広場からは、富山湾、立山連峰、新湊大橋、海王丸が一望できます。イベント広場は約18,200平方メートルあります。',
          source: 'https://www.kaiwomaru.jp/park/',
          mapQuery: '射水市 海王丸パーク',
        },
      ],
    },
    {
      id: 'hotaruika-kaijo',
      name: 'ほたるいか海上観光',
      area: '滑川市・4月の深夜',
      image: '/assets/scenery/toyama/scenes/10_hotaruika_kaijo.webp',
      hotspots: [
        {
          id: 'hotaru-kikan',
          x: 0.5,
          y: 0.52,
          title: '午前2時30分に出る船',
          body: 'ほたるいか海上観光の運航期間は4月初旬から5月初旬で、毎年期間は異なります。出航は午前2時30分から4時30分、所要は約90分から120分です。',
          source: 'https://www.city.namerikawa.toyama.jp/soshiki/13/2/5331.html',
          mapQuery: 'ほたるいか海上観光',
        },
        {
          id: 'hotaru-1000',
          x: 0.24,
          y: 0.38,
          title: '約1,000個の発光器',
          body: 'ホタルイカは深海200メートルから600メートルにすむ、胴長7センチメートル、重さ10グラムほどのイカです。体には約1,000個ものごく小さな発光器をもち、産卵の時期の3月から5月にかけて富山湾に集団でやってきます。',
          source: 'https://www.city.namerikawa.toyama.jp/soshiki/13/2/5329.html',
          mapQuery: '滑川市 ほたるいか',
        },
        {
          id: 'hotaru-tokuten',
          x: 0.76,
          y: 0.66,
          title: '国の特別天然記念物',
          body: '全国に分布しているホタルイカですが、これほど大量に押し寄せるのは世界的にも珍しいことです。この海域は「ホタルイカ群遊海面」と呼ばれ、国の特別天然記念物に指定されています。',
          source: 'https://www.city.namerikawa.toyama.jp/soshiki/13/2/5329.html',
          mapQuery: 'ホタルイカ群遊海面',
        },
      ],
    },
  ],
};
